/**
 * MessagesPanel (Admin)
 * Chat thread for WhatsApp messages between agent/admin and user.
 * Polls every 3s for new messages.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { conversationsAPI } from '../services/api';
import './MessagesPanel.css';

const MessagesPanel = ({ applicationId, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [windowActive, setWindowActive] = useState(false);
  const [hasWhatsapp, setHasWhatsapp] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);
  const msgsFingerprintRef = useRef('');

  const loadThread = useCallback(async () => {
    try {
      const res = await conversationsAPI.get(applicationId);
      const data = res.data;
      if (data.success) {
        setMessages(data.data.messages || []);
        setHasWhatsapp(data.data.hasWhatsapp);
        setWindowActive(data.data.windowActive || false);
      }
    } catch (err) {
      console.error('[MessagesPanel] loadThread error:', err);
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => {
    loadThread();
  }, [loadThread]);

  // Scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Poll every 3s
  useEffect(() => {
    const buildFingerprint = (msgs) =>
      msgs.map(m => `${m.id}:${m.media_url || ''}:${m.status || ''}`).join('|');

    const poll = setInterval(async () => {
      try {
        const res = await conversationsAPI.get(applicationId);
        const data = res.data;
        if (data.success) {
          const msgs = data.data.messages || [];
          const fp = buildFingerprint(msgs);
          if (fp !== msgsFingerprintRef.current) {
            setMessages(msgs);
            msgsFingerprintRef.current = fp;
            setWindowActive(data.data.windowActive || false);
          }
        }
      } catch (_) {}
    }, 3000);

    return () => clearInterval(poll);
  }, [applicationId]);

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    setSendError('');

    const optimistic = {
      id: Date.now(),
      direction: 'agent_to_user',
      message_type: 'text',
      content: text.trim(),
      status: 'sending',
      sent_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, optimistic]);
    const sentText = text.trim();
    setText('');

    try {
      const res = await conversationsAPI.send(applicationId, sentText);
      const data = res.data;
      if (!data.success) throw new Error(data.message || 'Failed to send');

      setMessages(prev => prev.map(m =>
        m.id === optimistic.id ? { ...m, status: 'sent', id: data.data?.messageId || m.id } : m
      ));
    } catch (err) {
      setSendError(err.response?.data?.message || err.message || 'Failed to send message');
      setMessages(prev => prev.filter(m => m.id !== optimistic.id));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="messages-panel">
      {/* Header */}
      <div className="messages-header">
        <div className="messages-header-left">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          <span className="messages-header-title">WhatsApp Chat</span>
          {!hasWhatsapp && <span className="messages-no-wa">No WhatsApp</span>}
        </div>
        <button className="messages-close-btn" onClick={onClose} title="Close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      {/* Thread */}
      <div className="messages-thread">
        {loading ? (
          <div className="messages-loading">Loading...</div>
        ) : messages.length === 0 ? (
          <div className="messages-empty">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{opacity: 0.3}}>
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            <p>No messages yet.</p>
            <p className="messages-empty-hint">Type a message below to contact the applicant via WhatsApp.</p>
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              className={`message-bubble ${msg.direction === 'agent_to_user' ? 'bubble-out' : 'bubble-in'}`}
            >
              {msg.media_url ? (
                <div className="message-media">
                  {msg.media_url.match(/\.(jpg|jpeg|png|gif|webp)/i) ? (
                    <img src={msg.media_url} alt="attachment" className="message-media-img" />
                  ) : (
                    <div className="message-media-file">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/>
                      </svg>
                      <span>{msg.media_filename || 'Attachment'}</span>
                    </div>
                  )}
                </div>
              ) : null}
              {msg.content && <p className="message-text">{msg.content}</p>}
              <div className="message-meta">
                <span className="message-time">{formatTime(msg.sent_at)}</span>
                {msg.direction === 'agent_to_user' && (
                  <span className={`message-status message-status-${msg.status}`}>
                    {msg.status === 'sending' ? '...' : msg.status === 'read' ? '✓✓' : msg.status === 'delivered' ? '✓✓' : '✓'}
                  </span>
                )}
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {/* Compose */}
      {sendError && <div className="messages-send-error">{sendError}</div>}
      <div className="messages-compose">
        <textarea
          className="messages-input"
          placeholder="Type a message... (Enter to send)"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={2}
          disabled={sending || !hasWhatsapp}
        />
        <button
          className="messages-send-btn"
          onClick={handleSend}
          disabled={!text.trim() || sending || !hasWhatsapp}
        >
          {sending ? (
            <span className="messages-send-spinner" />
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="22" y1="2" x2="11" y2="13"/>
              <polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          )}
        </button>
      </div>
    </div>
  );
};

export default MessagesPanel;
